/**
 * Page: HeroCarouselEdit
 * Author: Dine AI
 * Date: November 4, 2025
 * Last Modified: December 17, 2025 - Light mode conversion
 *
 * Purpose:
 * - Manage the 4 hero carousel items displayed to customers in Menu Assistant
 * - Inline editing for position (1-4) and badges (Popular, Chef's Special)
 * - Quick swap/reorder functionality
 * - Remove items from carousel
 *
 * Features:
 * - Shows only items marked as hero carousel (is_hero_carousel=true)
 * - Drag-and-drop reordering (future enhancement)
 * - Real-time preview of changes
 * - Auto-refreshes Redis cache on save (backend handles this)
 */
import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Edit2,
  Save,
  X,
  Sparkles,
  Star,
  ChefHat,
  Trash2,
  AlertCircle,
  Image as ImageIcon
} from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { apiClient } from '@/services/api/apiClient';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface HeroCarouselItem {
  menu_item_id: number;
  item_name: string;
  item_description: string;
  primary_price: number;
  image_url: string;
  hero_display_order: number;
  menu_type: 'regular' | 'seasonal';
  section: string;
  is_popular?: boolean;
  chef_reccom?: boolean;
  is_seasonal?: boolean;
  seasonal_menu_id?: number | null;
}

const HeroCarouselEdit = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [heroItems, setHeroItems] = useState<HeroCarouselItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingItems, setEditingItems] = useState<Set<number>>(new Set());
  const [saving, setSaving] = useState(false);
  const [itemToRemove, setItemToRemove] = useState<{ id: number; name: string } | null>(null);
  const [restaurantId, setRestaurantId] = useState<string | null>(null);

  useEffect(() => {
    const restId = localStorage.getItem('current_rest_id');
    if (!restId) {
      toast({
        title: 'Error',
        description: 'No restaurant selected',
        variant: 'destructive'
      });
      navigate('/restaurant-dashboard');
      return;
    }
    setRestaurantId(restId);
    fetchHeroItems(restId);

    // Scroll to highlighted item if specified in URL
    const itemId = searchParams.get('item');
    if (itemId) {
      setTimeout(() => {
        const element = document.getElementById(`item-${itemId}`);
        element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element?.classList.add('ring-2', 'ring-yellow-400');
        setTimeout(() => element?.classList.remove('ring-2', 'ring-yellow-400'), 2000);
      }, 500);
    }
  }, [searchParams]);

  const fetchHeroItems = async (restId: string) => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/menus/${restId}/hero-items`);
      setHeroItems(response.data.hero_items || []);
    } catch (error: any) {
      console.error('Error fetching hero items:', error);
      toast({
        title: 'Error',
        description: 'Failed to load hero carousel items',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleEdit = (itemId: number) => {
    setEditingItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const handleFieldChange = (itemId: number, field: keyof HeroCarouselItem, value: any) => {
    setHeroItems((prev) =>
      prev.map((item) =>
        item.menu_item_id === itemId ? { ...item, [field]: value } : item
      )
    );
  };

  const handleSave = async (item: HeroCarouselItem) => {
    try {
      setSaving(true);

      // Determine which endpoint to use based on menu type
      const endpoint = item.is_seasonal && item.seasonal_menu_id
        ? `/seasonal-menus/${item.seasonal_menu_id}/items/${item.menu_item_id}`
        : `/menus/${restaurantId}/items/${item.menu_item_id}`;

      await apiClient.put(endpoint, {
        is_popular: item.is_popular || false,
        chef_reccom: item.chef_reccom || false,
        is_hero_carousel: true, // Keep it in carousel
        hero_display_order: item.hero_display_order
      });

      // Refresh to sync with backend
      if (restaurantId) {
        await fetchHeroItems(restaurantId);
      }

      toast({
        title: 'Saved',
        description: `${item.item_name} updated successfully. Changes will appear in the Menu Assistant.`
      });

      toggleEdit(item.menu_item_id);
    } catch (error: any) {
      console.error('Error saving item:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to save changes',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveFromCarousel = async () => {
    if (!itemToRemove) return;

    try {
      setSaving(true);
      const item = heroItems.find((i) => i.menu_item_id === itemToRemove.id);
      if (!item) return;

      const endpoint = item.is_seasonal && item.seasonal_menu_id
        ? `/seasonal-menus/${item.seasonal_menu_id}/items/${item.menu_item_id}`
        : `/menus/${restaurantId}/items/${item.menu_item_id}`;

      await apiClient.put(endpoint, {
        is_hero_carousel: false, // Remove from carousel
        hero_display_order: null
      });

      if (restaurantId) {
        await fetchHeroItems(restaurantId);
      }

      toast({
        title: 'Removed',
        description: `${itemToRemove.name} removed from carousel`
      });

      setItemToRemove(null);
    } catch (error: any) {
      console.error('Error removing item:', error);
      toast({
        title: 'Error',
        description: 'Failed to remove item from carousel',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FDB913] mx-auto mb-4"></div>
          <p className="text-gray-500">Loading hero carousel items...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-8">
      {/* Standard Header - Light Mode */}
      <div className="border-b border-gray-200 bg-white shadow-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/restaurant-dashboard')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
              Back to Dashboard
            </button>
            <h1 className="text-xl font-semibold text-gray-900">
              Hero Carousel Manager
            </h1>
            <div className="w-32"></div> {/* Spacer for centering */}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
        {heroItems.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl border border-gray-200 shadow-sm p-12 text-center"
          >
            <div className="bg-gray-100 rounded-full p-6 w-24 h-24 mx-auto mb-4 flex items-center justify-center">
              <Sparkles className="h-12 w-12 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Carousel Items</h3>
            <p className="text-gray-500 mb-6">
              Mark menu items for the hero carousel in Edit Menu
            </p>
            <Button
              onClick={() => navigate('/edit-menu')}
              className="bg-[#FDB913] hover:bg-[#e5a711] text-black font-semibold"
            >
              Go to Edit Menu
            </Button>
          </motion.div>
        ) : (
          <div className="space-y-4">
            {/* Info Banner */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3"
            >
              <AlertCircle className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-gray-700 text-sm">
                  <strong>Carousel Order:</strong> Items are displayed in order from 1-4. Changes are automatically reflected in the Menu Assistant after saving.
                </p>
                <p className="text-gray-500 text-sm mt-2">
                  <strong>Adding Items:</strong> Add new items to this carousel from the Edit Menu page. The carousel supports up to 4 images, so please remove old items before adding new ones.
                </p>
              </div>
            </motion.div>

            {/* Hero Items List */}
            {heroItems.map((item, index) => {
              const isEditing = editingItems.has(item.menu_item_id);

              return (
                <motion.div
                  key={item.menu_item_id}
                  id={`item-${item.menu_item_id}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-xl border border-gray-200 shadow-sm p-6 transition-all"
                >
                  <div className="flex gap-6">
                    {/* Image */}
                    <div className="flex-shrink-0">
                      {item.image_url ? (
                        <img
                          src={item.image_url}
                          alt={item.item_name}
                          className="w-32 h-32 object-cover rounded-lg"
                        />
                      ) : (
                        <div className="w-32 h-32 bg-gray-100 rounded-lg flex items-center justify-center">
                          <ImageIcon className="h-12 w-12 text-gray-300" />
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h3 className="text-xl font-semibold text-[#FDB913] mb-1">
                            {item.item_name}
                          </h3>
                          <p className="text-gray-500 text-sm">
                            {item.section} • ${item.primary_price.toFixed(2)} • {item.menu_type === 'seasonal' ? 'Seasonal Menu' : 'Regular Menu'}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {isEditing ? (
                            <>
                              <Button
                                onClick={() => handleSave(item)}
                                disabled={saving}
                                size="sm"
                                className="bg-green-500 hover:bg-green-600 text-white"
                              >
                                <Save className="h-4 w-4 mr-1" />
                                Save
                              </Button>
                              <Button
                                onClick={() => toggleEdit(item.menu_item_id)}
                                disabled={saving}
                                size="sm"
                                variant="ghost"
                                className="text-gray-500 hover:bg-gray-100"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </>
                          ) : (
                            <Button
                              onClick={() => toggleEdit(item.menu_item_id)}
                              size="sm"
                              variant="ghost"
                              className="text-gray-600 hover:bg-gray-100"
                            >
                              <Edit2 className="h-4 w-4 mr-1" />
                              Edit
                            </Button>
                          )}
                        </div>
                      </div>

                      {/* Edit Fields */}
                      {isEditing && (
                        <div className="space-y-4 bg-gray-50 rounded-lg p-4 border border-gray-200">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Position */}
                            <div>
                              <label className="text-gray-700 text-sm mb-2 block font-medium">
                                Carousel Position
                              </label>
                              <Select
                                value={String(item.hero_display_order)}
                                onValueChange={(value) =>
                                  handleFieldChange(item.menu_item_id, 'hero_display_order', parseInt(value))
                                }
                              >
                                <SelectTrigger className="bg-white border-gray-200 text-gray-900">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="1">Position 1 (First)</SelectItem>
                                  <SelectItem value="2">Position 2</SelectItem>
                                  <SelectItem value="3">Position 3</SelectItem>
                                  <SelectItem value="4">Position 4</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>

                            {/* Badge Toggles - Stacked vertically */}
                            <div className="space-y-3">
                              {/* Popular Badge */}
                              <div className="flex items-center justify-between">
                                <div>
                                  <label className="text-gray-700 text-sm flex items-center gap-2 font-medium">
                                    <Star className="h-4 w-4 text-[#FDB913]" />
                                    Popular Badge
                                  </label>
                                  <p className="text-gray-400 text-xs mt-1">Show "POPULAR" badge</p>
                                </div>
                                <Switch
                                  checked={item.is_popular || false}
                                  onCheckedChange={(checked) =>
                                    handleFieldChange(item.menu_item_id, 'is_popular', checked)
                                  }
                                  className="data-[state=checked]:bg-[#FDB913] scale-75"
                                />
                              </div>

                              {/* Chef's Special Badge */}
                              <div className="flex items-center justify-between">
                                <div>
                                  <label className="text-gray-700 text-sm flex items-center gap-2 font-medium">
                                    <ChefHat className="h-4 w-4 text-[#FDB913]" />
                                    Chef's Special Badge
                                  </label>
                                  <p className="text-gray-400 text-xs mt-1">Show "CHEF'S SPECIAL" badge</p>
                                </div>
                                <Switch
                                  checked={item.chef_reccom || false}
                                  onCheckedChange={(checked) =>
                                    handleFieldChange(item.menu_item_id, 'chef_reccom', checked)
                                  }
                                  className="data-[state=checked]:bg-[#FDB913] scale-75"
                                />
                              </div>
                            </div>
                          </div>

                          {/* Remove from Carousel */}
                          <div className="pt-4 border-t border-gray-200">
                            <Button
                              onClick={() => setItemToRemove({ id: item.menu_item_id, name: item.item_name })}
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Remove from Carousel
                            </Button>
                          </div>
                        </div>
                      )}

                      {/* Badge Preview (when not editing) */}
                      {!isEditing && (
                        <div className="flex gap-2 flex-wrap">
                          <span className="px-3 py-1 bg-gray-100 rounded-full text-gray-600 text-sm font-medium">
                            Position #{item.hero_display_order}
                          </span>
                          {item.is_popular && (
                            <span className="px-3 py-1 bg-green-100 rounded-full text-green-600 text-sm flex items-center gap-1 font-medium">
                              <Star className="h-3 w-3" />
                              Popular
                            </span>
                          )}
                          {item.chef_reccom && (
                            <span className="px-3 py-1 bg-[#FDB913]/20 rounded-full text-[#b8860b] text-sm flex items-center gap-1 font-medium">
                              <ChefHat className="h-3 w-3" />
                              Chef's Special
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Remove Confirmation Dialog */}
      <AlertDialog open={!!itemToRemove} onOpenChange={() => setItemToRemove(null)}>
        <AlertDialogContent className="bg-white border-gray-200">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-gray-900">Remove from Carousel?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-500">
              Are you sure you want to remove <strong className="text-gray-700">{itemToRemove?.name}</strong> from the hero carousel?
              This item will no longer appear at the top of the Menu Assistant.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-gray-100 text-gray-700 hover:bg-gray-200 border-gray-200">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveFromCarousel}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default HeroCarouselEdit;
